import { ENTITLEMENT_LIST_SORT_ACTION_MAPPING } from '../../../../../../shared/constants/entitlements';
import { type ListTrackerState } from '../../../../../../shared/types';
import {
  type EntitlementPagination,
  type EntitlementSortState,
} from '../../../../../ViolationsEntitlement/data-layer/entitlementTypes';
import { type EntitlementUsersSidePanelRowContextState } from '../../../../data-layer/detailsTypes';
import { type UserItem } from '../data-layer/usersTypes';

/**
 * Users List Utility Functions
 *
 * Collection of utility functions for processing and transforming Users
 * data for API calls, UI display, and state management. Provides centralized
 * data transformation logic for the Users feature.
 *
 * Key Functions:
 * - API payload construction for Users list queries
 * - Filter data transformation and mapping
 * - Row tracker state configuration for UI interactions
 * - Side panel context data extraction
 * - Sort configuration and mapping
 *
 * Data Transformations:
 * - Converts filter states to API-compatible formats
 * - Maps UI sort options to backend sort parameters
 * - Extracts and formats data for side panel display
 * - Configures tracking metadata for list row interactions
 *
 * @fileoverview Utility functions for Users list data processing
 */

type FilterMapValueType = string[] | undefined;

/**
 * Creates filter payload for users list API
 * Transforms filter map to structured API format
 */
const getFilterPayload = (filterMap: Record<string, FilterMapValueType>) => ({
  userNames: filterMap.username ?? [],
  firstNames: filterMap.firstname ?? [],
  lastNames: filterMap.lastname ?? [],
  statuses: filterMap.status ?? [],
  displayNames: filterMap.displayname ?? [],
  departmentNames: filterMap.departmentname ?? [],
  emails: filterMap.email ?? [],
});

/**
 * Creates the complete payload for users list API call
 * Combines filters, pagination, and sorting parameters
 */
export const getUsersListPayload = (
  filterMap: Record<string, FilterMapValueType>,
  sortInAction?: EntitlementSortState,
  pagination: EntitlementPagination = { max: 25, offset: 0 }
) => {
  const filters = getFilterPayload(filterMap);

  return {
    pagination: {
      offset: pagination.offset,
      max: pagination.max,
    },
    sort: {
      field: sortInAction?.value ?? 'userName',
      order:
        ENTITLEMENT_LIST_SORT_ACTION_MAPPING[
          sortInAction?.order ?? ENTITLEMENT_LIST_SORT_ACTION_MAPPING.DESC
        ],
    },
    filters,
  };
};

/**
 * Configures tracker state for list rows
 * Adds UI interaction metadata to risk data
 */
export const configureUsersTracker = (
  users: UserItem[],
  isChecked = false
): (UserItem & {
  tracker: ListTrackerState;
})[] =>
  users.map((user, index) => ({
    ...user,
    tracker: {
      id: user.userKey?.toString() ?? '0',
      isChecked,
      isRowDetailsOpen: false,
      isTransition: false,
      isFirstAddded: index === 0,
      isReadOnlyAction: false,
    },
  }));

/**
 * Gets row context for side panel
 * Extracts relevant data for detailed view
 */
export const getUserRowContext = (
  user: UserItem
): EntitlementUsersSidePanelRowContextState => ({
  ...user,
});

/* SOD DonutChart methods */

/**
 * Truncates a number to specified decimal places without rounding
 * @param value - The number to truncate
 * @param decimalPlaces - Number of decimal places to keep (default: 1)
 * @returns Truncated number
 */
const truncateToDecimal = (value: number, decimalPlaces = 1): number => {
  const factor = 10 ** decimalPlaces;

  return Math.floor(value * factor) / factor;
};

/**
 * This function will return maximum of 6 digits
 * ex: 999.9K, 999.9M
 * @param number
 */
export const getAbbreviateNumber = (
  number: number,
  decimalPlaces = 1
): string => {
  if (!number) {
    return '0';
  }

  const absNumber = Math.abs(number);

  if (absNumber >= 1e9) {
    const truncated = truncateToDecimal(number / 1e9, decimalPlaces);

    return `${truncated.toString()}B`;
  }

  if (absNumber >= 1e6) {
    const truncated = truncateToDecimal(number / 1e6, decimalPlaces);

    return `${truncated.toString()}M`;
  }

  if (absNumber >= 1e3) {
    const truncated = truncateToDecimal(number / 1e3, decimalPlaces);

    return `${truncated.toString()}K`;
  }

  return truncateToDecimal(number, decimalPlaces).toString();
};

/**
 * Formats a percentage value with specified fraction digits
 * Returns integer values as strings without decimal places
 * @param value - The percentage value to format
 * @param fractionDigits - Number of decimal places (default: 1)
 * @returns Formatted percentage string
 */
export function formatPercentage(value: number, fractionDigits = 1) {
  return Number.isInteger(value)
    ? value.toString()
    : value.toFixed(fractionDigits);
}

/**
 * Calculates percentage from numerator and denominator
 * Handles division by zero and formats result
 * @param numerator - The numerator value
 * @param denominator - The denominator value
 * @param fractionDigits - Number of decimal places (default: 1)
 * @returns Formatted percentage string
 */
export const calculatePercentage = (
  numerator: number,
  denominator: number,
  fractionDigits = 1
): string => {
  if (denominator === 0) {
    return '0';
  }

  const percentage = (numerator / denominator) * 100;

  return percentage === 0
    ? '0'
    : `${parseFloat(formatPercentage(percentage, fractionDigits))}`;
};

/**
 * Converts string to title case (first letter of each word capitalized)
 * Handles multiple spaces and trims whitespace
 * @param value - The string to convert
 * @returns Title case formatted string
 */
export const getTitleCase = (value: string): string => {
  if (!value || typeof value !== 'string') {
    return value || '';
  }

  return value
    .trim()
    .split(/\s+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

export const usersDonutChartColorRange = [
  '#F25A00',
  '#FC3DA6',
  '#01AE66',
  '#D057FF',
  '#E19701',
];

/**
 * Transforms user violation data into donut chart format
 * Filters out categories with zero values
 * @param item - User violation details
 * @param l - Localization function
 * @returns Array of chart data with category and value
 */
export const getUserViolationChartData = (
  item: {
    openCount?: number | null;
    mitigatedCount?: number | null;
    closedCount?: number | null;
    inReviewCount?: number | null;
    remediatedCount?: number | null;
  },
  l: (key: string) => string
): { category: string; value: number }[] =>
  [
    { category: l('Open'), value: item.openCount ?? 0 },
    { category: l('Mitigated'), value: item.mitigatedCount ?? 0 },
    { category: l('Closed'), value: item.closedCount ?? 0 },
    { category: l('In Review'), value: item.inReviewCount ?? 0 },
    { category: l('Remediated'), value: item.remediatedCount ?? 0 },
  ].filter((d) => d.value > 0);
